import React, { useEffect, useMemo } from 'react';
import {
    matchRoutes,
    RouteObject,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';

import classnames from 'classnames/bind';

import {
    useGetDocumentByIdAdminLazy,
    useGetChapterByIdAdminLazy,
    useGetSectionByIdAdminLazy,
} from '@SERVICES';

import ROUTES from '@CONSTANTS/ROUTES.constant';

import Searchbar from '@COMPONENTS/HEADERS/breadcrumbs/AdminBreadclumbs/components/Searchbar';
import styles from './ModernAdminBreadcrumbs.module.scss';

const cx: CX = classnames.bind(styles);

type Breadcrumb = {
    id: string;
    title: string;
    subtitle?: string;
    route?: string;
    isActive: boolean;
    onClick?: () => void;
};

const routes: RouteObject[] = [
    { path: ROUTES.admin.documents.path },
    { path: ROUTES.admin.documents.id.path },
    { path: ROUTES.admin.chapters.id.path },
    { path: ROUTES.admin.sections.id.path },
];

type SectionParent = {
    id: number;
    name: string;
    parent: SectionParent | null;
};

function buildSectionHierarchy(
    parent: SectionParent | null,
    accumulator: { title: string; id: number }[],
): { title: string; id: number }[] {
    if (!parent) {
        return accumulator;
    }

    const newAccumulator = [
        {
            title: parent.name,
            id: parent.id,
        },
        ...accumulator,
    ];

    return buildSectionHierarchy(parent.parent, newAccumulator);
}

function ModernAdminBreadcrumbs() {
    const [getDocument, { data: documentData }] = useGetDocumentByIdAdminLazy();
    const [getChapter, { data: chapterData }] = useGetChapterByIdAdminLazy();
    const [getSection, { data: sectionData }] = useGetSectionByIdAdminLazy();

    const sectionParentNames = useMemo(() => buildSectionHierarchy(
        sectionData?.data.parent as SectionParent | null,
        [
            {
                title: sectionData?.data?.name || '',
                id: sectionData?.data?.id || 0,
            },
        ],
    ), [sectionData?.data?.parent, sectionData?.data?.name, sectionData?.data?.id]);

    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const [{ route }] = matchRoutes(routes, location) || [{ route: { path: ROUTES.admin.documents.path } }];

    const isDocumentsListPage = route.path === ROUTES.admin.documents.path;
    const isDocumentPage = route.path === ROUTES.admin.documents.id.path;
    const isChapterPage = route.path === ROUTES.admin.chapters.id.path;
    const isSectionPage = route.path === ROUTES.admin.sections.id.path;

    useEffect(() => {
        const { id } = params;

        if (isDocumentPage && id) {
            getDocument(+id, true);
        } else if (isChapterPage && id) {
            getChapter(+id, true);
        } else if (isSectionPage && id) {
            getSection(+id, true);
        }
    }, [isDocumentPage, isChapterPage, isSectionPage, getDocument, getChapter, getSection, params]);

    const breadcrumbs: Breadcrumb[] = useMemo(() => {
        const crumbs: Breadcrumb[] = [];

        // Always start with Documents List
        crumbs.push({
            id: 'documents-list',
            title: 'Document Templates',
            isActive: isDocumentsListPage,
            onClick: () => navigate(ROUTES.admin.documents.path),
        });

        // Add Document if we have document data or are on document+ pages
        if (isDocumentPage || isChapterPage || isSectionPage) {
            const documentName = documentData?.data?.name || chapterData?.data?.document?.name || sectionData?.data?.document?.name;

            let documentRoute: string | undefined;
            if (documentData?.data?.id) {
                documentRoute = ROUTES.admin.documents.id.path.replace(':id', documentData.data.id.toString());
            } else if (chapterData?.data?.document?.id) {
                documentRoute = ROUTES.admin.documents.id.path.replace(':id', chapterData.data.document.id.toString());
            } else if (sectionData?.data?.document?.id) {
                documentRoute = ROUTES.admin.documents.id.path.replace(':id', sectionData.data.document.id.toString());
            }

            crumbs.push({
                id: 'document',
                title: 'Document',
                subtitle: documentName,
                isActive: isDocumentPage,
                route: documentRoute,
                onClick: () => {
                    const docId = documentData?.data?.id || chapterData?.data?.document?.id || sectionData?.data?.document?.id;
                    if (docId) {
                        navigate(ROUTES.admin.documents.id.path.replace(':id', docId.toString()));
                    }
                },
            });
        }

        // Add Chapter if we have chapter data or are on chapter+ pages
        if (isChapterPage || isSectionPage) {
            const chapterName = chapterData?.data?.name || sectionData?.data?.chapter?.name;

            let chapterRoute: string | undefined;
            if (chapterData?.data?.id) {
                chapterRoute = ROUTES.admin.chapters.id.path.replace(':id', chapterData.data.id.toString());
            } else if (sectionData?.data?.chapter?.id) {
                chapterRoute = ROUTES.admin.chapters.id.path.replace(':id', sectionData.data.chapter.id.toString());
            }

            crumbs.push({
                id: 'chapter',
                title: 'Chapter',
                subtitle: chapterName,
                isActive: isChapterPage,
                route: chapterRoute,
                onClick: () => {
                    const chapterId = chapterData?.data?.id || sectionData?.data?.chapter?.id;
                    if (chapterId) {
                        navigate(ROUTES.admin.chapters.id.path.replace(':id', chapterId.toString()));
                    }
                },
            });
        }

        // Add Section if we have section data
        if (isSectionPage && sectionParentNames.length > 0) {
            // Add parent sections first
            sectionParentNames.slice(0, -1).forEach((parentSection) => {
                crumbs.push({
                    id: `section-${parentSection.id}`,
                    title: 'Section',
                    subtitle: parentSection.title,
                    isActive: false,
                    onClick: () => {
                        navigate(ROUTES.admin.sections.id.path.replace(':id', parentSection.id.toString()));
                    },
                });
            });

            // Add current section
            const currentSection = sectionParentNames[sectionParentNames.length - 1];
            crumbs.push({
                id: `section-${currentSection.id}`,
                title: 'Section',
                subtitle: currentSection.title,
                isActive: true,
            });
        }

        return crumbs;
    }, [
        isDocumentsListPage,
        isDocumentPage,
        isChapterPage,
        isSectionPage,
        documentData,
        chapterData,
        sectionData,
        sectionParentNames,
        navigate,
    ]);

    function getSearchType() {
        if (isDocumentPage) return 'document';
        if (isChapterPage) return 'chapter';
        if (isSectionPage) return 'section';
        return null;
    }

    return (
        <div className={cx('modern-admin-breadcrumbs')}>
            <div className="container max-constraint center">
                <div className={cx('content')}>
                    <div className={cx('breadcrumbs-section')}>
                        <div className={cx('breadcrumbs-wrapper')}>
                            {breadcrumbs.map((crumb, index) => (
                                <div key={crumb.id} className={cx('breadcrumb-item')}>
                                    {crumb.onClick ? (
                                        <div
                                            className={cx('breadcrumb-content', {
                                                'is-active': crumb.isActive,
                                                'is-clickable': !crumb.isActive && crumb.onClick,
                                            })}
                                            onClick={crumb.onClick}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <div className={cx('breadcrumb-main')}>
                                                <span className={cx('breadcrumb-title')}>{crumb.title}</span>
                                                {crumb.subtitle && (
                                                    <span className={cx('breadcrumb-subtitle')}>
                                                        {crumb.subtitle}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className={cx('breadcrumb-content', {
                                                'is-active': crumb.isActive,
                                                'is-clickable': !crumb.isActive && crumb.onClick,
                                            })}
                                        >
                                            <div className={cx('breadcrumb-main')}>
                                                <span className={cx('breadcrumb-title')}>{crumb.title}</span>
                                                {crumb.subtitle && (
                                                    <span className={cx('breadcrumb-subtitle')}>
                                                        {crumb.subtitle}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {index < breadcrumbs.length - 1 && (
                                        <div className={cx('breadcrumb-separator')}>
                                            <svg
                                                width="6"
                                                height="10"
                                                viewBox="0 0 6 10"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M1 1L5 5L1 9"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {!isDocumentsListPage && (
                        <div className={cx('search-section')}>
                            <Searchbar
                                id={+params.id!}
                                searchType={getSearchType()}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ModernAdminBreadcrumbs; 